import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import {
  Badge,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseAllIcon,
  ExpandAllIcon,
  SearchIcon,
  TextInput,
  XIcon,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { useAccAccountTree, type AccAccountTreeNode } from '../accounts/useData';
import {
  collectExpandableNodeIds,
  filterAccountTree,
  flattenAccountTree,
  type TreeListRow,
} from '../accounts/treeListUtils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  value?: string | number;
  postingOnly?: boolean;
  onSelect: (accountId: number) => void;
};

const isSelectableAccount = (row: TreeListRow, postingOnly: boolean) =>
  row.isActive !== false && (!postingOnly || row.isPosting);

export const CoaAccountPickerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  value,
  postingOnly = true,
  onSelect,
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const { data: tree, isLoading } = useAccAccountTree({ lite: true });

  const valueId = value != null && String(value) !== '' ? Number(value) : null;

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setPendingId(valueId);
    setHighlightedId(valueId);
    if (tree.length > 0) {
      setCollapsedIds(new Set(collectExpandableNodeIds(tree)));
    }
  }, [isOpen, valueId, tree]);

  const filteredTree = useMemo(() => filterAccountTree(tree, searchTerm), [tree, searchTerm]);
  const isSearchActive = searchTerm.trim().length > 0;
  const expandableIds = useMemo(() => collectExpandableNodeIds(tree), [tree]);

  const rows = useMemo(() => {
    const collapsed = isSearchActive ? new Set<number>() : collapsedIds;
    return flattenAccountTree(filteredTree, collapsed);
  }, [filteredTree, collapsedIds, isSearchActive]);

  const selectableRows = useMemo(
    () => rows.filter((row) => isSelectableAccount(row, postingOnly)),
    [rows, postingOnly],
  );

  const pendingRow = useMemo(
    () => (pendingId != null ? rows.find((row) => row.id === pendingId) ?? null : null),
    [pendingId, rows],
  );

  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(() => setCollapsedIds(new Set(expandableIds)), [expandableIds]);

  const chooseRow = useCallback((row: TreeListRow) => {
    if (!isSelectableAccount(row, postingOnly)) return;
    setPendingId(row.id);
    setHighlightedId(row.id);
  }, [postingOnly]);

  const confirmSelection = useCallback(() => {
    if (pendingId == null) return;
    onSelect(pendingId);
    onClose();
  }, [pendingId, onSelect, onClose]);

  const moveHighlight = useCallback((direction: 1 | -1) => {
    if (selectableRows.length === 0) return;
    setHighlightedId((current) => {
      const idx = current != null ? selectableRows.findIndex((row) => row.id === current) : -1;
      if (idx < 0) return selectableRows[direction === 1 ? 0 : selectableRows.length - 1].id;
      const next = (idx + direction + selectableRows.length) % selectableRows.length;
      return selectableRows[next].id;
    });
  }, [selectableRows]);

  useEffect(() => {
    if (highlightedId != null) setPendingId(highlightedId);
  }, [highlightedId]);

  useEffect(() => {
    if (!isOpen || highlightedId == null) return;
    document.getElementById(`coa-picker-row-${highlightedId}`)?.scrollIntoView({ block: 'nearest' });
  }, [highlightedId, isOpen, rows]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (pendingId != null) confirmSelection();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <div className="min-w-0">
          <ModalTitle>Select account</ModalTitle>
          <p className={cn('text-sm mt-0.5', colors.textMuted)}>
            Browse the chart of accounts and pick a posting account.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1 rounded-lg transition-colors shrink-0', colors.textSecondary, colors.interactiveHover)}
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>

      <ModalContent className="!pt-4">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[14rem]">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search code or name…"
                prefix={<SearchIcon className="h-4 w-4" />}
                showClearButton={Boolean(searchTerm)}
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={expandAll} title="Expand all">
              <ExpandAllIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={collapseAll} title="Collapse all">
              <CollapseAllIcon className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" className="font-normal tabular-nums">
              {selectableRows.length} posting
            </Badge>
          </div>

          <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
            <div className={cn('max-h-[min(52vh,28rem)] overflow-y-auto dashboard-scrollbar', colors.surface)}>
              {isLoading && (
                <p className={cn('px-4 py-8 text-sm text-center', colors.textMuted)}>Loading chart of accounts…</p>
              )}

              {!isLoading && rows.length === 0 && (
                <p className={cn('px-4 py-8 text-sm text-center', colors.textMuted)}>No accounts found.</p>
              )}

              {!isLoading && rows.map((row) => {
                const selectable = isSelectableAccount(row, postingOnly);
                const isPending = pendingId === row.id;
                const isHighlighted = highlightedId === row.id;
                const codeLabel = row.combLevel ?? row.code;

                return (
                  <div
                    key={row.id}
                    id={`coa-picker-row-${row.id}`}
                    role="option"
                    aria-selected={isPending}
                    aria-disabled={!selectable}
                    onClick={() => {
                      if (selectable) chooseRow(row);
                      else if (row.hasChildren) toggleCollapsed(row.id);
                    }}
                    onDoubleClick={() => {
                      if (!selectable) return;
                      chooseRow(row);
                      onSelect(row.id);
                      onClose();
                    }}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 border-b last:border-b-0 text-sm transition-colors',
                      colors.border,
                      selectable ? 'cursor-pointer' : 'cursor-default opacity-80',
                      isPending
                        ? cn(colors.info, 'ring-1 ring-inset', colors.primaryBorderAll)
                        : isHighlighted
                          ? cn(colors.surfaceHover)
                          : selectable
                            ? colors.interactiveHover
                            : '',
                    )}
                    style={{ paddingLeft: 12 + row.depth * 18 }}
                  >
                    <span className="w-6 shrink-0 flex items-center justify-center">
                      {row.hasChildren ? (
                        <button
                          type="button"
                          className={cn('p-0.5 rounded', colors.textMuted, colors.interactiveHover)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapsed(row.id);
                          }}
                          aria-label={collapsedIds.has(row.id) ? 'Expand' : 'Collapse'}
                        >
                          {collapsedIds.has(row.id) && !isSearchActive ? (
                            <ChevronRightIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>
                      ) : null}
                    </span>

                    <span className={cn('font-mono text-xs tabular-nums shrink-0 min-w-[4.5rem]', colors.textSecondary)}>
                      {codeLabel}
                    </span>

                    <span className={cn('flex-1 min-w-0 truncate', colors.text)} title={row.name}>
                      {row.name}
                    </span>

                    {!row.isPosting && (
                      <Badge variant="secondary" className="font-normal shrink-0 text-[10px] px-1.5 py-0">
                        Header
                      </Badge>
                    )}
                    {row.isPosting && isPending && (
                      <Badge variant="info" className="font-normal shrink-0 text-[10px] px-1.5 py-0">
                        Selected
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <p className={cn('text-xs', colors.textMuted)}>
            Click to select, double-click to confirm, or use the button below. Header accounts are shown for context but cannot be posted to.
          </p>
        </div>
      </ModalContent>

      <ModalFooter className="justify-between gap-3">
        <div className={cn('min-w-0 text-sm', colors.textMuted)}>
          {pendingRow ? (
            <>
              <span className={cn('font-medium', colors.text)}>
                {pendingRow.name}
              </span>
            </>
          ) : (
            'No account selected'
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={confirmSelection} disabled={pendingId == null}>
            Select account
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
